import { type Cold } from '@owl-nest/cold-data'
import { type SlimCold } from '@owl-nest/cold-data/client.ts'
import * as shopify from '@owl-nest/shopify'
import * as utils from '@owl-nest/utils'
// Do not change this export to `@owl-nest/utils` because some other utils
// depends on the design system. We don't expect `contentful-shopify-matcher` to
// need jsx transformation to be executed
import * as textUtils from '@owl-nest/utils/src/text.ts'

// TODO: create & replace with @owl-nest/url package ?
import * as url from '@boutique/url'

import * as base from '../base.ts'
import * as tag from '../tag/index.ts'
import { buildTags } from '../utils.ts'

export type MediumProduct = {
  availableForSale: boolean
  brand: string
  compareAtPriceRange: [number, number] | null
  category: base.Taxonomy | null
  criteria: {
    environmental: tag.MediumTag<tag.ExtraPillar>[]
    social: tag.MediumTag<tag.ExtraPillar>[]
  }
  description: string
  id: string
  handle: string
  href: string
  picture: base.Image
  priceRange: [number, number]
  tags: tag.PartialTag[]
  realTags: tag.PartialTag[]
  title: string
  vendor: string
}

export function mediumProduct(product: shopify.product.MediumProduct, cold: Cold | SlimCold): MediumProduct {
  const { categories, environmentalPillars, productTypes, socialPillars, others } = buildTags(
    product.tags.map((tag) => utils.text.slugify(tag)),
    cold,
  )

  const brand = Object.values(cold.brands).find(
    (brand) =>
      // This is a duplication of the 'matchBrand' function in `contentful-shopify-matcher` but
      // this package belongs to ulule/boutique and imports mustn't go that way
      // (from boutique to owl-nest).
      textUtils.slugify(brand.identifier ?? brand.name, '-') ===
      textUtils.slugify(product.brandName?.value ?? product.vendor ?? '', '-'),
  )?.identifier

  function formatProductTags(): tag.PartialTag[] {
    return [
      // Types
      ...categories.map((tag) => formatTag(tag, 'category')),
      ...productTypes.map((tag) => formatTag(tag, 'types')),
      // Other tags
      ...environmentalPillars.map((tag) => formatTag(tag, 'environmentalPillar')),
      ...socialPillars.map((tag) => formatTag(tag, 'socialPillar')),
      ...others,
    ]
  }

  return {
    availableForSale: product.availableForSale,
    brand: brand ?? product.brandName?.value ?? product.vendor,
    category: categories.length > 0 ? formatTag(categories[0], 'category') : null,
    compareAtPriceRange: getCompareAtPriceRange(product),
    criteria: {
      environmental: environmentalPillars || [],
      social: socialPillars || [],
    },
    description: product.descriptionHtml,
    handle: product.handle,
    href: url.product({
      handle: product.handle,
      id: shopify.utils.toNumericId(product.id, 'Product'),
      title: product.title,
    }).path,
    id: product.id,
    picture: {
      alt: product.images?.edges[0]?.node.altText || '',
      src: product.images?.edges[0]?.node.originalSrc || '',
      srcSet: `${product.images?.edges[0]?.node.transformedSrc1x} 1x, ${product.images?.edges[0]?.node.transformedSrc2x} 2x, ${product.images?.edges[0]?.node.transformedSrc3x} 3x`,
      origin: product.images?.edges[0]?.node.altText || '',
      width: null,
      height: null,
    },
    priceRange: product.priceRange
      ? [Number(product.priceRange.minVariantPrice.amount), Number(product.priceRange.maxVariantPrice.amount)]
      : [0, 0],
    tags: formatProductTags(),
    realTags: product.tags.reduce((realTags, slug) => {
      if (cold.tags[slug] !== undefined) {
        realTags.push(cold.tags[slug])
      }
      return realTags
    }, [] as tag.PartialTag[]),
    title: product.title,
    vendor: product.vendor,
  }
}

function getCompareAtPriceRange(product: shopify.product.MediumProduct): [number, number] | null {
  const minVariantPriceAmount = Number(product.priceRange.minVariantPrice.amount)

  const minVariantCompareAtPriceAmount = parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
  const maxVariantCompareAtPriceAmount = parseFloat(product.compareAtPriceRange.maxVariantPrice.amount)

  if (minVariantCompareAtPriceAmount === 0 && maxVariantCompareAtPriceAmount === 0) return null

  if (minVariantPriceAmount >= minVariantCompareAtPriceAmount) {
    // if (minVariantPriceAmount !== minVariantCompareAtPriceAmount) {
    //     logger.warn(
    //         `[Shopify] variants minimum 'price' is higher than its minimum 'compareAtPrice' for product ${product.id}`,
    //         {
    //             product,
    //             values: {
    //                 minPrice: minVariantPriceAmount,
    //                 minCompareAtPrice: minVariantCompareAtPriceAmount
    //             }
    //         }
    //     )
    // }
    return null
  }

  return [minVariantCompareAtPriceAmount, maxVariantCompareAtPriceAmount]
}

function formatTag<T extends base.Taxonomy>(tag: T, kind: string): base.Tag {
  return {
    kind,
    name: tag.name,
    slug: tag.slug,
  }
}
